import React from 'react';
import clsx from 'clsx';
import { IOrganizationData } from '@/types/organization.types';
import Select from 'react-select';
import { IntlShape } from 'react-intl';

interface DMOAdminFieldsProps {
  formData: Partial<IOrganizationData>;
  errors: { [key: string]: string };
  isCreating: boolean;
  isRegionsLoading: boolean;
  regionOptions: Array<{ value: string; label: string }>;
  handleInputChange: (
    e: React.ChangeEvent<
      HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement
    >
  ) => void;
  handleLinkedRegionsChange: (
    selected: readonly { value: string; label: string }[]
  ) => void;
  handleBlur: (name: string) => void;
  mode: 'view' | 'edit' | 'create';
  showOnlyRemainingFields?: boolean;
  intl: IntlShape;
}

const organisationTypeOptions = [
  { value: 'DMO', label: 'DMO' },
  { value: 'Regional Tourism Board', label: 'Regional Tourism Board' },
  { value: 'City Marketing', label: 'City Marketing' },
];

const DMOAdminFields = ({
  formData,
  errors,
  isCreating,
  isRegionsLoading,
  regionOptions,
  handleInputChange,
  handleLinkedRegionsChange,
  handleBlur,
  mode,
  showOnlyRemainingFields = false,
  intl, // Destructure intl prop
}: DMOAdminFieldsProps) => {
  const isViewMode = mode === 'view';

  return (
    <>
      {!showOnlyRemainingFields && (
        <>
          <div>
            <label
              htmlFor="organisationName-input"
              className="block text-sm font-medium text-gray-700 mb-1"
            >
              {intl.formatMessage({ id: 'ORGANIZATION.FORM.NAME' })}
            </label>
            <input
              id="organisationName-input"
              type="text"
              name="organisation_name"
              autoComplete="organization"
              value={formData.organisation_name || ''}
              disabled={isCreating || isViewMode}
              onChange={handleInputChange}
              onKeyDown={(e) => {
                if (
                  e.key === ' ' &&
                  (e.target as HTMLInputElement).value === ''
                ) {
                  e.preventDefault();
                }
              }}
              onBlur={() => handleBlur('organisation_name')}
              className={clsx(
                'input input-bordered w-full h-10 text-sm border-gray-300 focus:ring-primary focus:border-primary',
                errors.organisation_name && 'border-red-500 focus:ring-red-500',
                (isViewMode || isCreating) ? 'bg-gray-100 cursor-not-allowed' : 'bg-white'
              )}
              aria-invalid={!!errors.organisation_name}
              aria-describedby={
                errors.organisation_name ? 'organisation_name-error' : undefined
              }
              placeholder={intl.formatMessage({
                id: 'ORGANIZATION.FORM.NAME.PLACEHOLDER',
              })}
              data-testid="organisation-name-input"
            />
            {errors.organisation_name && (
              <p
                id="organisation_name-error"
                className="text-red-500 text-sm mt-1"
              >
                {errors.organisation_name}
              </p>
            )}
          </div>
          <div>
            <label
              htmlFor="organisationType-input"
              className="block text-sm font-medium text-gray-700 mb-1"
            >
              {intl.formatMessage({ id: 'ORGANIZATION.FORM.TYPE' })}
            </label>
            <select
              id="organisationType-input"
              name="organisation_type"
              value={formData.organisation_type || ''}
              onChange={handleInputChange}
              onBlur={() => handleBlur('organisation_type')}
              disabled={isCreating || isViewMode}
              className={clsx(
                'select select-bordered w-full h-10 text-sm border-gray-300 focus:ring-primary focus:border-primary',
                errors.organisation_type && 'border-red-500 focus:ring-red-500',
                (isViewMode || isCreating) ? 'bg-gray-100 cursor-not-allowed' : 'bg-white'
              )}
              aria-invalid={!!errors.organisation_type}
              aria-describedby={
                errors.organisation_type ? 'organisation_type-error' : undefined
              }
            >
              <option value="" disabled>
                {intl.formatMessage({ id: 'ORGANIZATION.FORM.TYPE.SELECT' })}
              </option>
              {organisationTypeOptions.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
            {errors.organisation_type && (
              <p
                id="organisation_type-error"
                className="text-red-500 text-sm mt-1"
              >
                {errors.organisation_type}
              </p>
            )}
          </div>
        </>
      )}
      <div>
        <label
          htmlFor="contactEmail-input"
          className="block text-sm font-medium text-gray-700 mb-1"
        >
          {intl.formatMessage({ id: 'ORGANIZATION.FORM.CONTACT_EMAIL' })}
        </label>
        <input
          id="contactEmail-input"
          type="email"
          name="contact_email"
          autoComplete="email"
          value={formData.contact_email || ''}
          disabled={isCreating || isViewMode}
          onChange={handleInputChange}
          onBlur={() => handleBlur('contact_email')}
          className={clsx(
            'input input-bordered w-full h-10 text-sm border-gray-300 focus:ring-primary focus:border-primary',
            errors.contact_email && 'border-red-500 focus:ring-red-500',
            (isViewMode || isCreating) ? 'bg-gray-100 cursor-not-allowed' : 'bg-white'
          )}
          aria-invalid={!!errors.contact_email}
          aria-describedby={
            errors.contact_email ? 'contact_email-error' : undefined
          }
          placeholder={intl.formatMessage({
            id: 'ORGANIZATION.FORM.CONTACT_EMAIL.PLACEHOLDER',
          })}
        />
        {errors.contact_email && (
          <p id="contact_email-error" className="text-red-500 text-sm mt-1">
            {errors.contact_email}
          </p>
        )}
      </div>
      <div className="mb-4">
        <label
          htmlFor="linkedRegions-input"
          className="block text-sm font-medium text-gray-700 mb-1"
        >
          {intl.formatMessage({ id: 'ORGANIZATION.FORM.LINKED_REGIONS' })}
        </label>
        <Select
          inputId="linkedRegions-input"
          isMulti
          options={regionOptions}
          value={regionOptions.filter(
            (option: { value: string; label: string }) =>
              formData.linked_regions?.includes(option.value)
          )}
          onChange={handleLinkedRegionsChange}
          onBlur={() => handleBlur('linked_regions')}
          isDisabled={isCreating || isRegionsLoading || isViewMode}
          isLoading={isRegionsLoading}
          className={clsx(
            'basic-multi-select text-sm',
            errors.linked_regions ? 'border-red-500' : '',
            isViewMode && 'bg-gray-100 cursor-not-allowed'
          )}
          classNamePrefix="select"
          placeholder={intl.formatMessage({
            id: isRegionsLoading
              ? 'ORGANIZATION.FORM.LINKED_REGIONS.LOADING'
              : 'ORGANIZATION.FORM.LINKED_REGIONS.SELECT',
          })}
          noOptionsMessage={() =>
            intl.formatMessage({
              id: 'ORGANIZATION.FORM.LINKED_REGIONS.NONE',
            })
          }
          styles={{
            control: (base) => ({
              ...base,
              fontSize: '14px',
            }),
            option: (base) => ({
              ...base,
              fontSize: '14px',
            }),
            placeholder: (base) => ({
              ...base,
              fontSize: '14px',
            }),
            input: (base) => ({
              ...base,
              fontSize: '14px',
            }),
          }}
          aria-invalid={!!errors.linked_regions}
          aria-describedby={
            errors.linked_regions ? 'linked_regions-error' : undefined
          }
          isClearable={false}
          data-testid="linked-regions-input"
        />
        {errors.linked_regions && (
          <p id="linked_regions-error" className="mt-1 text-sm text-red-500">
            {errors.linked_regions}
          </p>
        )}
      </div>
      <div>
        <label
          htmlFor="publicHomepage-input"
          className="block text-sm font-medium text-gray-700 mb-1"
        >
          {intl.formatMessage({ id: 'ORGANIZATION.FORM.PUBLIC_HOMEPAGE' })}
        </label>
        <input
          id="publicHomepage-input"
          type="url"
          name="public_homepage"
          autoComplete="off"
          value={formData.public_homepage || ''}
          disabled={isCreating || isViewMode}
          onChange={handleInputChange}
          onBlur={() => handleBlur('public_homepage')}
          className={clsx(
            'input input-bordered w-full h-10 text-sm border-gray-300 focus:ring-primary focus:border-primary',
            errors.public_homepage && 'border-red-500 focus:ring-red-500',
            (isViewMode || isCreating) ? 'bg-gray-100 cursor-not-allowed' : 'bg-white'
          )}
          aria-invalid={!!errors.public_homepage}
          aria-describedby={
            errors.public_homepage ? 'public_homepage-error' : undefined
          }
          placeholder={intl.formatMessage({
            id: 'ORGANIZATION.FORM.PUBLIC_HOMEPAGE.PLACEHOLDER',
          })}
        />
        {errors.public_homepage && (
          <p id="public_homepage-error" className="text-red-500 text-sm mt-1">
            {errors.public_homepage}
          </p>
        )}
      </div>
      <div>
        <label
          htmlFor="description-input"
          className="block text-sm font-medium text-gray-700 mb-1"
        >
          {intl.formatMessage({ id: 'ORGANIZATION.FORM.DESCRIPTION' })}
        </label>
        <textarea
          id="description-input"
          name="description"
          value={formData.description || ''}
          disabled={isCreating || isViewMode}
          onChange={handleInputChange}
          onBlur={() => handleBlur('description')}
          className={clsx(
            'textarea textarea-bordered w-full h-24 text-sm border-gray-300 focus:ring-primary focus:border-primary',
            errors.description && 'border-red-500 focus:ring-red-500',
            (isViewMode || isCreating) ? 'bg-gray-100 cursor-not-allowed' : 'bg-white'
          )}
          aria-invalid={!!errors.description}
          aria-describedby={
            errors.description ? 'description-error' : undefined
          }
          placeholder={intl.formatMessage({
            id: 'ORGANIZATION.FORM.DESCRIPTION.PLACEHOLDER',
          })}
        />
        {errors.description && (
          <p id="description-error" className="text-red-500 text-sm mt-1">
            {errors.description}
          </p>
        )}
      </div>
    </>
  );
};

export default DMOAdminFields;
